import { resources, common, enums } from '../../../gads'
import { GadsFieldsRequired } from '../../../utils/global'
export type AdComparisonRecActions = PauseAd.Body | WriteAd.Body

export namespace GeneralErrors {
    export const Error = {
        NO_TOP_SPENDING_AD_GROUPS: 'no_top_spending_ad_groups',
        NOT_ENOUGH_AD_TYPES_IN_AD_GROUP: 'not_enough_ad_types_in_ad_group',
        FAILED_TO_GENERATE_ANY_IMPROVEMENTS_FOR_DOMAIN:
            'failed_to_generate_any_improvements_for_domain',
        NO_EXTRA_DETAILS_FOR_WRITE_IMPLEMENT: 'no_extra_details_for_write_implement',
    } as const

    export type Error = (typeof Error)[keyof typeof Error]

    export const ErrorReadable = {
        [Error.NO_TOP_SPENDING_AD_GROUPS]: 'There are no top spending ad groups for this domain',
        [Error.NOT_ENOUGH_AD_TYPES_IN_AD_GROUP]: 'There are not enough ad types in this domain',
        [Error.FAILED_TO_GENERATE_ANY_IMPROVEMENTS_FOR_DOMAIN]:
            'No improvements were generated for this domain',
        [Error.NO_EXTRA_DETAILS_FOR_WRITE_IMPLEMENT]:
            'extra details (new ad copy) expected when writing new ad',
    }
}

export namespace PauseAd {
    export interface Body {
        domain_id: number
        adgroup_id: string
        campaign_id: number
        overlapping_days: number
        confidence: number
        measure_mode: MeasureMode
        change: number
        ad_type: string
        mobile: boolean
        worst_ad: Ad
        best_ad: Ad
        campaign_label: string
        adgroup_label: string
        adgroup_30d_cost: number
    }

    export interface ExtraDetails {
        pause_worst_ad: boolean
    }

    export enum MeasureMode {
        'CPI' = 'cpi',
        'VPI' = 'vpi',
        'CTR' = 'ctr',
    }

    export interface Ad {
        resource_name: string
        id: number
        image_name: string | undefined
        image_url: string | undefined
        image_width: number | undefined
        image_height: number | undefined
        raw_clicks: number
        clicks: number
        impressions: number
        cost: number
        impr_abs_top: number
        impr_top: number
        ctr: number
        raw_ctr: number
        conversions: number
        conversion_value: number
        conversion_rate: number
        cost_conversion: number
        cpc: number
        vpi: number
        cpi: number
        cpa: number
        device_preference: string
        headline: string | undefined
        expanded_text_ad_headline3: string | undefined
        headline_part_1: string | undefined
        headline_part_2: string | undefined
        description: string | undefined
        expanded_text_ad_description2: string | undefined
        path_1: string | undefined
        path_2: string | undefined
        root_domain: string
    }

    export interface BestAndWorstAd {
        best_ad: Ad
        worst_ad: Ad
        overlapping_days: number
        campaign_id: number
        confidence: number
        change: number
        ad_type: string
        mobile: boolean
        campaign_label: string
        adgroup_label: string
        measure_mode: MeasureMode
    }

    export interface AdVpi {
        vpi: number
        metrics: TotalMetrics
        ad: AdMetric
    }
    export interface AdCpi {
        cpi: number
        metrics: TotalMetrics
        ad: AdMetric
    }
    export interface AdCtr {
        ctr: number
        metrics: TotalMetrics
        ad: AdMetric
    }
}

export namespace WriteAd {
    export interface Body {
        ad_group_resource_name: string
        domain_id: number
        ad_id: number
        adgroup_id: number
        campaign_id: number
        clicks: number
        impressions: number
        impressions_absolute_top: number
        impressions_top: number
        ad_type: enums.AdType
        cost: number
        ctr: number
        campaign_label: string
        adgroup_label: string
        expanded_text_ad_headline1: string | undefined
        expanded_text_ad_headline2: string | undefined
        expanded_text_ad_headline3: string | undefined
        expanded_text_ad_description: string | undefined
        expanded_text_ad_description2: string | undefined
        expanded_text_ad_path_1: string | undefined
        expanded_text_ad_path_2: string | undefined
        root_domain: string
        adgroup_30d_cost: number
        top_search_terms: { text: string; impressions: number }[]
    }

    export interface ExtraDetails {
        headline_part_1: string
        headline_part_2: string
        expanded_text_ad_headline3: string
        path_1: string
        path_2: string
        description: string
        expanded_text_ad_description2: string
        destination_url: string
    }
}

export interface AdGroup {
    ad_group: GadsFieldsRequired<resources.AdGroup, 'resource_name' | 'id'>
    campaign: GadsFieldsRequired<
        resources.Campaign,
        'resource_name' | 'id' | 'advertising_channel_sub_type'
    >
    metrics: GadsFieldsRequired<common.Metrics, 'cost_micros'>
    segments: GadsFieldsRequired<common.Segments, 'conversion_action'>
}

export interface AdMetric {
    ad_group: GadsFieldsRequired<resources.AdGroup, 'resource_name' | 'id' | 'name'>
    ad_group_ad: {
        resource_name: string
        ad: GadsFieldsRequired<
            resources.Ad,
            | 'resource_name'
            | 'device_preference'
            | 'id'
            | 'type'
            | 'final_urls'
            | 'expanded_text_ad'
            | 'image_ad'
        >
    }
    metrics: GadsFieldsRequired<
        common.Metrics,
        | 'absolute_top_impression_percentage'
        | 'all_conversions'
        | 'all_conversions_value'
        | 'clicks'
        | 'cost_micros'
        | 'impressions'
        | 'top_impression_percentage'
    >

    campaign: GadsFieldsRequired<resources.Campaign, 'resource_name' | 'id' | 'name'>
    segments: GadsFieldsRequired<common.Segments, 'date'>
}

export interface GroupedAdMetric {
    [ad_group_id: string]: AdMetric[]
}

export interface AdGroupAdTypes {
    [ad_group_id: string]: { ad_type: enums.AdType }[]
}

export interface AdTypeCounts {
    ETA: number
    TA: number
    IA: number
}

export interface AdGroupAdTypeCounts {
    [ad_group_id: string]: AdTypeCounts
}

export interface SuggestedRecAction {
    ad_group_id: string
    suggested: string
}

export interface TotalMetrics {
    all_conversions: number
    all_conversions_value: number
    impressions: number
    clicks: number
    cost: number
    top_impression_average: number
    weighted_top_impression_average: number
    top_absolute_impression_average: number
    weighted_top_absolute_impression_average: number
    thirty_day_cost: number
}
